import type { HowItWorksContent } from "../types";

export const howItWorks: HowItWorksContent = {
  meta: {
    // [de-review] SERP-visible. Title must stay <= 60 chars and the description
    // <= 160, which the existing seo.test.tsx cap pins for every locale.
    title: "So funktioniert's | Flex Fund Prop-Firm-Evaluation",
    description:
      "Vier Schritte von der Gebühr zur Finanzierung — Konto wählen, 1-Phasen- oder 2-Phasen-Evaluation bestehen, echtes Kapital traden und wöchentlich auszahlen.",
  },
  hero: {
    line1: "So wirst du",
    line2Highlight: "finanziert.",
    lede: "Vier Schritte. Keine Überraschungen. Jede Regel an Tag 1 dieselbe wie an Tag 1.000.",
  },
  steps: {
    stepLabel: "SCHRITT",
    cards: {
      chooseAndPay: {
        title: "Wählen & zahlen",
        desc: "Modell und Größe wählen. Einmalige Gebühr. Kein Abo, keine versteckten Kosten.",
        metaLeft: "5 Minuten",
        // [de-review] The entry price.
        metaRight: "ab 79 $",
      },
      passEvaluation: {
        title: "Evaluation bestehen",
        desc: "Erreiche das Gewinnziel und bleibe dabei innerhalb der Drawdown- und Risikolimits.",
        metaLeft: "Kein Zeitlimit",
        metaRight: "1-Phase oder 2-Phasen",
      },
      tradeLive: {
        title: "Echtes Kapital traden",
        desc: "MetaTrader 5 mit STP-Ausführung und Tier-1-Liquidität. Es gelten die Regeln für finanzierte Konten.",
        metaLeft: "Live MT5",
        metaRight: "STP · Tier-1",
      },
      withdrawShare: {
        title: "Anteil auszahlen",
        // [de-review] States the payout processing window.
        desc: "Feste Auszahlungsfenster. Banküberweisung oder USDC / USDT, Bearbeitung in 48–72 Geschäftsstunden.",
        metaLeft: "Feste Fenster",
        metaRight: "80–90 % Split",
      },
    },
  },
  rules: {
    title: { line1: "1-Phase oder 2-Phasen.", line2Highlight: "Gleiche Auszahlungen." },
    headers: {
      rule: "REGEL",
      oneStep: "1-PHASE",
      oneStepSub: "Schnellerer Weg",
      twoStep: "2-PHASEN",
      twoStepSub: "Bessere Konditionen",
    },
    labels: {
      profitTarget: "Gewinnziel",
      dailyLoss: "Tagesverlustlimit",
      maxDrawdown: "Max. Drawdown",
      minTradingDays: "Min. Handelstage",
      profitSplit: "Profit-Split",
      timeLimit: "Zeitlimit",
      weekendHolding: "Halten über das Wochenende",
    },
    words: { none: "Keins", allowed: "Erlaubt", upTo: "Bis zu" },
  },
  example: {
    title: { lead: "Anatomie eines ", highlight: "2-Phasen-Zyklus", tail: "." },
    // [de-review] Anchors every figure in the worked example to the dashboard.
    sub: "Ein Trader mit 100.000 $ · 2-Phasen, aufgeschlüsselt an jedem Checkpoint. Jede Zahl entspricht dem, was dein Dashboard anzeigen würde.",
    steps: {
      payFee: {
        phase: "KAUF",
        title: "Gebühr zahlen",
        body: "Wähle die 100.000 $ · 2-Phasen-Evaluation. Einmalige Gebühr, kein Abo, keine wiederkehrenden Kosten.",
        keyLabel: "EINMALIGE GEBÜHR",
      },
      phase1: {
        phase: "PHASE 1",
        title: "8 % Gewinn erreichen",
        body: "Erreiche 108.000 $ aus einem Startkapital von 100.000 $. Kein Zeitdruck.",
        keyLabel: "ZIEL PHASE 1",
      },
      phase2: {
        phase: "PHASE 2",
        title: "5 % Gewinn erreichen",
        body: "Frisches Guthaben von 100.000 $. Erreiche 105.000 $, um Konstanz zu bestätigen und den finanzierten Status freizuschalten.",
        keyLabel: "ZIEL PHASE 2",
      },
      withdraw: {
        phase: "FINANZIERT",
        title: "80 % auszahlen",
        body: "Erwirtschafte 5.000 $ über einen Auszahlungszyklus auf dem finanzierten Konto.",
        keyLabel: "DEIN ANTEIL",
      },
    },
    // [de-review] The scaling disclaimer under the worked example.
    footnote:
      "Zahlen basieren auf dem 100.000-$-·-2-Phasen-Programm beim Standard-Split von 80 %. Andere Kontogrößen skalieren linear. Die obigen Prozentsätze gelten für jedes gewählte Startkapital.",
  },
  scaling: {
    title: { lead: "Alle 4 Monate berechtigt. ", highlight: "Bis zu 1 Mio. $." },
    sub: "Konstante Trader schalten größere Allokationen nach einem dokumentierten Zeitplan frei. Keine Verhandlungen, keine willkürlichen Meilensteine.",
    milestonePath: "Meilenstein-Pfad",
    ladder: {
      // The "$10K – $200K" and "$1M" deltas are figures and stay identical to
      // the English; only the words around them change.
      funded: { cycle: "FINANZIERT", size: "Deine Startstufe", delta: "$10K – $200K" },
      mo4: { cycle: "+4 MON", size: "Erste Skalierung", delta: "Berechtigt" },
      mo8: { cycle: "+8 MON", size: "Zweite Skalierung", delta: "Berechtigt" },
      mo12: { cycle: "+12 MON", size: "Dritte Skalierung", delta: "Berechtigt" },
      mo20: { cycle: "+20 MON", size: "Bis zur Flaggschiff-Grenze", delta: "$1M" },
    },
    eligibilityTitle: "Berechtigung · alle 4 Monate",
    eligibilitySub: "Vier Checkpoints erfüllen, um die nächste Allokation freizuschalten.",
    // [de-review] The four scaling criteria are contractual conditions.
    criteria: [
      "+10 % Nettogewinn über den Zyklus",
      "2 erfolgreiche Auszahlungen abgeschlossen",
      "Positives Guthaben gehalten",
      "Keine Regelverstöße im Zeitraum",
    ],
    note: "Risikolimits skalieren proportional mit deinem neuen Guthaben. Es gelten dieselben Prozentregeln, nur die absoluten Zahlen wachsen.",
  },
  faq: {
    title: { lead: "Häufige ", highlight: "Fragen", tail: "." },
    sub: "Durchsuche die vollständige Bibliothek in der Flex Fund Wissensdatenbank. Tiefere Antworten, mehr Detail.",
    browseAll: "Alle FAQs ansehen",
    // [de-review] All six answers state rules, payout terms or the split. The
    // category tags are display labels, not slugs — the deep-link `path` stays
    // in data.ts and is never translated.
    items: {
      timeLimit: {
        q: "Gibt es ein Zeitlimit für die Evaluation?",
        a: "Nein. Professionelle Trader handeln, wenn der Markt einen Vorteil bietet, nicht wenn eine Stoppuhr es vorgibt. Nimm dir so viel Zeit, wie du brauchst, um das Ziel zu erreichen.",
        category: "REGELN",
      },
      payoutFrequency: {
        q: "Wie oft werden Auszahlungen bearbeitet?",
        a: "Wöchentlich. Deine erste Auszahlung ist an Tag 7 des Handels auf deinem finanzierten Konto verfügbar, danach kannst du alle 7 Tage auszahlen. Die Beträge werden per Banküberweisung, USDC/USDT oder Karte gesendet.",
        category: "AUSZAHLUNGEN",
      },
      profitSplit: {
        q: "Welchen Profit-Split bekomme ich?",
        a: "Jedes Konto startet unabhängig von der Größe mit einem 80-%-Split. Über das optionale Add-on beim Checkout kannst du auf 90 % upgraden. Ein Hebel, keine Skalierungsstufen, keine Überraschungen. Der gewählte Split gilt für die gesamte Laufzeit des Kontos.",
        category: "AUSZAHLUNGEN",
      },
      oneVsTwoStep: {
        q: "Was ist der Unterschied zwischen 1-Phase und 2-Phasen?",
        a: "2-Phasen ist eine zweiphasige Evaluation (Ziel 8 %, dann 5 %) mit konservativen Drawdown-Limits. 1-Phase komprimiert die Evaluation auf ein einzelnes Ziel von 10 % mit strengeren Risikoregeln. Höhere Gebühr, schnellerer Weg zur Finanzierung.",
        category: "REGELN",
      },
      easCopyTrading: {
        q: "Darf ich EAs und Copy-Trading nutzen?",
        a: "Ja, Expert Advisors und Copy-Trading sind in beiden Evaluationsmodellen erlaubt. Es gelten die üblichen Einschränkungen bei News-Ereignissen und HFT-artigem Missbrauch. Alle Details stehen im Trader-Agreement.",
        category: "PLATTFORM",
      },
      ruleBreach: {
        q: "Was passiert, wenn ich gegen eine Regel verstoße?",
        a: "Bei einem Verstoß gegen den maximalen Drawdown ist die Kontoschließung endgültig, dein Trader-Profil und deine Historie werden aber auf das nächste Konto übertragen. Kostenlose Resets bieten wir nicht an. Die Preise sind einmalig und bleiben transparent.",
        category: "REGELN",
      },
    },
  },
};
